import { AnalyticsBrowser, AnalyticsBrowserSettings, InitOptions } from '@segment/analytics-next';

import {
  createDQC01Traits,
  createDQC10Traits,
  createTQC03Properties,
  createTQC04Properties,
  createTQC06Properties,
} from './segment-trait-mappers';
import { AnalyticsData, CreditTerms, Offer } from '../../domain/qualification';
import { getCookie } from '../../general/utils/cookies';
import { RootState } from '../../store';

const analytics = new AnalyticsBrowser();

export type Traits = ReturnType<typeof createDQC01Traits> | { email: string };

type Integrations = { Heap: boolean; All?: boolean };
type Context = {
  coastMsgType: 'DQC09' | 'DQC01' | 'DQC10' | 'TQC03' | 'TQC04' | 'TQC05' | 'TQC06';
};
type Options = { integrations?: Integrations; context?: Context };

const getCookieProperties = () => ({
  statsig_stable_id: getCookie('statsig_stable_id'),
  hubspotutk: getCookie('hubspotutk'),
});

const identify = async ({ userId, traits, options = {} }: { userId?: string; traits: Traits; options?: Options }) => {
  await clearUserTraits();
  await analytics.identify(userId, { ...traits, ...getCookieProperties() }, options);
};

const clearUserTraits = async () => {
  const user = await analytics.user();
  return user.traits({});
};

// Do NOT call more than once (https://github.com/segmentio/analytics-next/tree/master/packages/browser#lazy--delayed-loading)
const loadAnalytics = (settings: AnalyticsBrowserSettings, options: InitOptions = {}) => {
  analytics.load(settings, options);
};

const resetAnalytics = async () => analytics.reset();

const sendDQC01 = async ({ analyticsData, state }: { analyticsData: AnalyticsData; state: RootState }) => {
  const traits = createDQC01Traits({ analyticsData, state });
  const options: Options = {
    context: { coastMsgType: 'DQC01' },
    integrations: { Heap: false },
  };
  return identify({ userId: analyticsData.userId, traits, options });
};

const sendDQC10 = async ({ analyticsData: { userId }, state }: { analyticsData: AnalyticsData; state: RootState }) => {
  const traits = createDQC10Traits(state);
  const options: Options = {
    context: { coastMsgType: 'DQC10' },
    integrations: { Heap: true, All: false },
  };
  return identify({ userId, traits, options });
};

const sendTQC05 = (paylaod: { testimonialPanelExperiment: string }) => {
  const properties = { ...paylaod, ...getCookieProperties() };
  const options: Options = {
    context: { coastMsgType: 'TQC05' },
    integrations: { Heap: false },
  };

  return analytics.track('Get Started', properties, options);
};

const sendTQC03 = (state: RootState) => {
  const properties = { ...createTQC03Properties(state), ...getCookieProperties() };
  const options: Options = {
    context: { coastMsgType: 'TQC03' },
    integrations: { Heap: false },
  };

  return analytics.track('Business Info 2 Submitted', properties, options);
};

const sendTQC04 = ({
  state,
  creditTerms,
  offers,
}: {
  state: RootState;
  creditTerms: Pick<CreditTerms, 'billingCycle' | 'finalCreditLimit'>;
  offers?: Offer[];
}) => {
  const properties = {
    ...createTQC04Properties({ creditTerms, state, offers }),
    ...getCookieProperties(),
  };
  const options: Options = {
    context: { coastMsgType: 'TQC04' },
    integrations: { Heap: false },
  };

  return analytics.track('Application Prequalified', properties, options);
};

const sendTQC06 = (offerSelected: number) => {
  const properties = {
    ...createTQC06Properties(offerSelected),
    ...getCookieProperties(),
  };
  const options: Options = {
    context: { coastMsgType: 'TQC06' },
    integrations: { Heap: false },
  };

  return analytics.track('Offer Selected', properties, options);
};

const sendPage = () => analytics.page(undefined, undefined, getCookieProperties());

export { loadAnalytics, resetAnalytics, sendDQC01, sendDQC10, sendTQC03, sendTQC04, sendTQC05, sendTQC06, sendPage };
